import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { find, findIndex, isEmpty, isEqual, isNil, isUndefined, map, sortBy, replace } from "lodash"
import { Area, Bar, Brush, CartesianGrid, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, } from 'recharts';
import { useTheme } from "@mui/material/styles";
import { gridLine } from "@plannel/components/dashboard/chartTokens";
import { DASH_PATTERN } from "@plannel/components/rechart/ChartComposed";
import reduxUtil from "@plannel/utils/redux-util";
import numberUtil from "@plannel/utils/number-util";
import debug from "@plannel/utils/debug-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ToggleButton from "@plannel/components/ToggleButton";
import tabMenuList from "@plannel/pages/TabMenuList";
import p13nService from "@plannel/services/system/p13n-service";
import ChartCommonConfigModal from "@plannel/components/modal/ChartCommonConfigModal";
import { SettingsButton, FullScreenButton } from "@plannel/components/ActionIconButton";
import { AgStyleTooltipContent } from "@plannel/components/rechart/AgStyleTooltip";
import chartP13nSignature from "@plannel/components/rechart/chartP13nSignature";
import { FS } from "@plannel/utils/font-util";

/*
 * 막대 폭 상한. **고정 폭이 아니다** — recharts 가 범주 칸 폭에서 계산한 값을 여기서 자르기만 한다.
 *   예전에는 `barSize={30}` 으로 못 박아서, 화면이 넓어져도 막대는 30px 그대로고 빈칸만 넓어졌다
 *   (실측: 플롯 2414px ÷ 12칸 = 칸당 200px 인데 막대 2개가 60px → 칸의 70%가 공백).
 *   상한만 두면 범주가 적을 때 막대가 과하게 뚱뚱해지는 것도 같이 막힌다.
 */
const MAX_BAR_SIZE = 40;
// 막대 윗모서리. 누적(stackId) 막대에는 주지 않는다 — 중간 토막까지 둥글어져 단이 어긋나 보인다.
const BAR_RADIUS = [4, 4, 0, 0];
const CHART_FONT_SIZE = FS.SUB;

const { colorSet, colorByMeasure } = ColorUtil;
// area 차트 그라데이션 fill 용 id (ChartComposed 와 동일 규칙 — 위 0.28 → 아래 0 투명).
const gradKey = (f) => `pivotCaGrad-${String(f).replace(/[^\w-]/g, "_")}`;
const ComposedBarLineChart = (props) => {
  const {
    data,
    options,
    pivotConfig,
    nullConvert,
    customDataFormatter,
    referenceLine,
    tooltipAnimation,
    toggle,
    customTooltip,
    allDataSeries,
    defaultSeries,
    showConfigurationPopup,
    fullScreen,
    setFullScreen,
    screenId,
    setScreenId,
    chartClickEvent,
    multiSeries = true,
    brush = false,
    activeMeasures
  } = props;
  const [legend, setLegend] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartPivotConfigs, setChartPivotConfigs] = useState({});
  const [chartId, setChartId] = useState("");
  const [chartOptions, setChartOptions] = useState(options ?? {});
  const [chartModalResultData, setChartModalResultData] = useState({});

  const tabState = reduxUtil.getTabState();
  const activeTab = tabState.tabKeys[tabState.tabIndex];
  const viewName = tabMenuList.findLv3Menu(activeTab).reduxKey;
  const hasKey = options?.key ? true : false; // key 값이 존재하는 경우 chartData의 p13n key값을 기준으로 rendering

  /*
   * 마지막으로 읽은 p13n 응답. 언어 변경은 pivotConfig 안의 **번역문만** 바꾸므로 재조회 사유가
   * 아니다. 그릴 데이터(data)와 시리즈 구성(field 목록)이 그대로면 보관본으로 다시 조립한다.
   */
  const p13nCacheRef = useRef({ sig: null, data: null, content: null });
  const seriesSig = chartP13nSignature(
    viewName,
    allDataSeries ?? pivotConfig?.chartSeries,
    [options?.key ?? "", String(multiSeries)]
  );
  const baseFormatter = (number) => {
    return numberUtil.formatNumber(number);
  }

  const getSeriesByLegend = (yAxisDataKey, type) => {
    return yAxisDataKey.map((dataKey) => {
      const obj = {};

      obj.field = dataKey;
      obj.activeFlg = true;
      obj.text = dataKey;
      obj.type = type;
      return obj;
    });
  }

  const pivotDataToChartData = (p13nData) => {
    const chartDataArray = [];
    const pivotXAxis = [];
    let yAxisDataKey = [];
    let activeSeries = {};

    const targetSeries = allDataSeries ? allDataSeries : pivotConfig?.chartSeries;
    let tempChartId = "";
            
    if (!data[0]) {
      return chartDataArray;
    }
    Object.entries(data[0]).forEach((el) => {
      if (pivotConfig.include.includes(el[0])) {
        pivotXAxis.push(el[0]);
      }
    });

    pivotXAxis.sort();

    /* uniqueKey config가 존재하는 경우 사용되는 변수들 */
    const yAxisDataCombinedLegend = []; /* Combined(uniqueKey + groupKey) Legend */
    const yAxisDataGroupKeyLegend = []; /* GroupKey Legend */
    const yAxisDataModifiedLegend = []; /* (MainLegend) 기본적으로 GroupKey를 사용하고 GroupKey가 겹치는 조건일때 Combined Key를 사용하는 Legend */
    data.forEach((item) => {
      const groupKey = item[pivotConfig.groupKey];
      const uniqueKey = item[pivotConfig?.uniqueKey];

      /* unique config Key가 있는 경우 */
      if(!isEmpty(uniqueKey)) {
        const combinedKey = `(${uniqueKey})${groupKey}`;
        if (yAxisDataCombinedLegend.indexOf(combinedKey) < 0) {
          yAxisDataCombinedLegend.push(combinedKey);

          const prevYAxisKeyIndex = yAxisDataGroupKeyLegend.indexOf(groupKey);
          if (prevYAxisKeyIndex < 0) {
            yAxisDataGroupKeyLegend.push(groupKey);
            yAxisDataModifiedLegend.push(groupKey);
          } else {
            /* unique Key는 다르고 groupKey는 동일한경우 */
            /* 이전에 중복된 Key 값 또한 combined key로 변경*/
            yAxisDataModifiedLegend[prevYAxisKeyIndex] = yAxisDataCombinedLegend[prevYAxisKeyIndex];
            yAxisDataModifiedLegend.push(combinedKey);
            yAxisDataGroupKeyLegend.push(groupKey);
          }
        }
      } else {
        if (yAxisDataKey.indexOf(groupKey) < 0) {
          yAxisDataKey.push(groupKey);
        }
      }
    });

    if(!isEmpty(pivotConfig?.uniqueKey)) {
      yAxisDataKey = yAxisDataModifiedLegend;
    }

    if (targetSeries && multiSeries) {
      if (hasKey) {
        tempChartId = options?.key;
      } else {
        targetSeries.forEach((series, idx) => {
          if (idx === targetSeries.length - 1) {
            tempChartId = `${tempChartId}${series.field}Chart`
          } else {
            tempChartId += series.field;
          }
        });
      }
      setChartId(tempChartId);
      if (p13nData?.chart?.[tempChartId]) {
        const series = []; 
        const orgSeries = [];

        const targetChart = p13nData.chart[tempChartId];

        targetSeries.forEach((target) => {
          const p13nSeriesData = targetChart[target.field]; 
          if (p13nSeriesData) {
            const result = {...target, type: p13nSeriesData.type.toLowerCase()}
            orgSeries.push(result);
            if (p13nSeriesData.activeFlg) {
              series.push(result);
            }
          }
        });

        const configs = {...pivotConfig, chartSeries: series, chartOrgSeries: orgSeries};
        setChartPivotConfigs(configs);
      } else {
        const series = [];
        const configSeries = defaultSeries ?? pivotConfig.chartSeries;
        const orgSeries = targetSeries.map((target) => {
          const usedSeries = configSeries.filter(usedData => usedData.field === target.field);

          if (usedSeries.length === 0) {
            target.activeFlg = false;
          } else {
            series.push(usedSeries[0]);
          }

          return target;
        });

        // chartOrgSeries = 설정 모달 행 순서의 원본 — **type 으로 정렬하지 않는다.**
        //   정렬하면 차트 타입을 바꿔 저장할 때마다 모달 목록 순서가 뒤바뀐다(모달이 이 순서를 그대로 쓴다).
        const configs = {...pivotConfig, chartSeries: series, chartOrgSeries: orgSeries};
        setChartPivotConfigs(configs);
      }

      setLegend(yAxisDataKey);
    } else { // for case :: MonthlyBySku
      const configs = { ...pivotConfig };
      tempChartId = "chart";
      setChartId(tempChartId);

      if (p13nData?.chart?.[tempChartId]) {
        const orgSeries = [];
        const targetChart = p13nData.chart[tempChartId];

        if (targetSeries) {
          targetSeries.forEach((target) => {
            const p13nSeriesData = targetChart[target.field];
            if (p13nSeriesData) {
              const result = { ...target, ...p13nSeriesData, text: target.text };
              orgSeries.push(result);
              if (p13nSeriesData.activeFlg) {
                activeSeries = result;
                configs.yAxisLabel = result.text;
              }
            }
          });

          configs.chartDisplaySeries = getSeriesByLegend(yAxisDataKey, activeSeries?.type);
          configs.chartOrgSeries = orgSeries;
        }

        setChartPivotConfigs(configs);
        setLegend(yAxisDataKey);
      } else {
        if (targetSeries) {
          activeSeries = targetSeries.find(usedData => usedData.activeFlg);
          const orgSeries = getSeriesByLegend(yAxisDataKey, activeSeries?.type);
          configs.yAxisLabel = activeSeries.text;
          configs.chartDisplaySeries = orgSeries;
          configs.chartOrgSeries = targetSeries;
        } else {
          const orgSeries = getSeriesByLegend(yAxisDataKey, null);

          configs.chartOrgSeries = orgSeries;
        }

        setChartPivotConfigs(configs);
        setLegend(yAxisDataKey);
      }
    }

    let targetData = [...data];
    if (!multiSeries) { // for case :: MonthlyBySku
      if (isEmpty(activeSeries)) {
        targetData = [];
      } else {
        targetData = data.filter(target => isEqual(target?.originMeasure ?? target.measure, activeSeries.field));
      }
    }

    pivotXAxis.forEach((xAxisName, index) => {
      const dataObj = {};
      targetData.forEach((item, itemIndex) => {
        if (nullConvert) {
          dataObj[yAxisDataKey[itemIndex]] = item[xAxisName] ? item[xAxisName] : 0;
        } else {
          dataObj[yAxisDataKey[itemIndex]] = item[xAxisName];
        }
        dataObj.currencySymbol = item?.currency
      });

      if (!chartOptions?.decimalPoint) {
        for (const entry of Object.entries(dataObj)) {
          const value = entry[1];
          if (isUndefined(Math.round(value)) || isNaN(Math.round(value))) {
            continue;
          }
          dataObj[entry[0]] = Math.round(value);
        }
      }
      chartDataArray.push({
        name: pivotXAxis[index],
        ...dataObj
      })
    });
    return chartDataArray;
  }

  const sortXAxis = baseDates => {
    const xAxisMap = pivotConfig.xAxisSortMap;
    const sortHeaders = Object.keys(xAxisMap).sort();

    const sortedData = [];
    for (let idx = 0; idx < baseDates.length; idx++) {
      const sortData = baseDates.find(baseData => isEqual(baseData.name, xAxisMap[sortHeaders[idx]]));
      if (sortData) {
        sortedData.push(sortData);
      }
    }

    return sortedData;
  }

  const setData = configData => {
    let convertedData = pivotDataToChartData(configData);
    if (pivotConfig?.xAxisSortMap) {
      convertedData = sortXAxis(convertedData);
    }

    setChartData(convertedData);
  }

  useEffect(() => {
    if (data.length > 0) {
      if (pivotConfig) {
        const cache = p13nCacheRef.current;
        // 언어만 바뀐 경우 — 네트워크 없이 보관본으로 다시 조립한다.
        if (isEqual(cache.sig, seriesSig) && cache.data === data && !isNil(cache.content)) {
          setData(cache.content);
          return;
        }

        p13nService.get(viewName)
        .then(response => {
          p13nCacheRef.current = { sig: seriesSig, data, content: response.data };
          setData(response.data);
        })
        .catch(e => {
          debug(e);
        });
      }
    } else {
      setChartData([]);
      const fallbackSeries = allDataSeries ?? pivotConfig?.chartSeries;
      if (!isEmpty(fallbackSeries)) {
        setChartPivotConfigs(prev => ({
          ...prev,
          chartOrgSeries: fallbackSeries
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, pivotConfig, seriesSig]);

  useEffect(() => {
    if (chartModalResultData.reload) {
      // 설정 모달이 저장 직후 내용을 직접 넘겨준다. 보관본도 같이 갱신해 두어야 다음 언어 변경 때
      // 옛 설정으로 되돌아가지 않는다.
      p13nCacheRef.current = { sig: seriesSig, data, content: chartModalResultData.content };
      setData(chartModalResultData.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartModalResultData])

  useEffect(() => {
    const defaultOptions = {
      margin: { left: 10, top: 25, bottom: brush ? 20 : 10, right: 30 },
      xAxisPadding: { left: 30, right: 30 },
      // 라벨은 눕힌다. -10° 는 공간을 아껴 주지도 못하면서 축만 삐뚤어 보이게 했다.
      //   ⚠ 각도만 바꾸고 `xAxisHeight` 는 건드리지 말 것 — 범례가 `bottom: 30` 고정이라
      //     이 높이 안에 같이 들어앉는다. 34 로 줄였더니 눈금 라벨이 범례를 14px 파고들었다.
      xAxisAngle: 0,
      xAxisHeight: 60,
      xAxisDy: 10,
      xAxisInterval: "preserveEnd",
      yAxisDomain: [0, 'auto'],
      minWidth: "760px",
      minHeight: "240px"
    }
    if (options) {
      if (options?.mode && isEqual(options.mode, "fill")) {
        const fillModeOptions = {
          margin: { left: 20, top: 20, right: 20 },
          xAxisHeight: 40,
        }
        setChartOptions({ ...defaultOptions, ...fillModeOptions, ...options });
      } else {
        setChartOptions({ ...defaultOptions, ...options });
      }
    } else {
      setChartOptions(defaultOptions);
    }

  }, [options, brush]);

  return (
    <PivotChartContainer chartData={chartData} legend={legend} baseFormatter={customDataFormatter ? customDataFormatter : baseFormatter}
      fullScreen={fullScreen} setFullScreen={setFullScreen} screenId={screenId} setScreenId={setScreenId}
      pivotConfig={chartPivotConfigs} options={chartOptions} referenceLine={referenceLine ? referenceLine : null}
      tooltipAnimation={tooltipAnimation} toggle={toggle} customTooltip={customTooltip}
      viewName={viewName} setChartModalResultData={setChartModalResultData} chartId={chartId}
      showConfigurationPopup={showConfigurationPopup} multiSeries={multiSeries} brush={brush} chartClickEvent={chartClickEvent} activeMeasures={activeMeasures}
      allDataSeries={allDataSeries} />
  );
}

const PivotChartContainer = ({ chartData, legend, pivotConfig, baseFormatter, options, referenceLine, tooltipAnimation, toggle, customTooltip, viewName, setChartModalResultData, chartId, showConfigurationPopup, multiSeries, fullScreen, setFullScreen, screenId, setScreenId, brush, chartClickEvent, activeMeasures, allDataSeries }) => {
  const theme = useTheme();
  const isFilled = options?.mode && isEqual(options.mode, "fill");
  const [sortedLegend, setSortedLegend] = useState([]);
  const [formatters, setFormatters] = useState([]);
  const [yAxisInfo, setYAxisInfo] = useState([]);
  const [toggled, setToggled] = useState(toggle? CodeUtil.toggleType.BAR : "");
  const [chartConfigOpen, setChartConfigOpen] = useState(false);
  const [chartOptions, setChartOptions] = useState(options);
  const [seriesAnanlysisObj, setSeriesAnanlysisObj] = useState({});

  const referenceX = referenceLine ? referenceLine.x : null;
  const referenceY = referenceLine ? referenceLine.y : null;
  const yAxisList = [];
  const showFullScreen = isUndefined(fullScreen) ? false : true;
  const showPopup = showConfigurationPopup === false ? false : true;
  const dataOrgSeries = isUndefined(pivotConfig?.chartOrgSeries) ? [] : pivotConfig.chartOrgSeries;
  const dataDisplaySeries = isUndefined(pivotConfig?.chartDisplaySeries) ? [] : pivotConfig.chartDisplaySeries;
  /*
   * 색 인덱스 — **차트 타입·활성 여부와 무관해야 한다.**
   * 렌더 순번(sortedLegend)은 type 으로 정렬된 목록이고 pivotConfig.chartSeries 는 활성만 담긴 목록이라,
   * 그 순번을 색 인덱스로 쓰면 **차트 타입만 바꿔도 손대지 않은 시리즈까지 색이 밀린다**
   * (colorByMeasure 는 미등록 measure·비default 팔레트에서 인덱스 색을 쓴다).
   * → 페이지가 선언한 원본 순서에서 뽑는다. 목록에 없으면 기존 순번으로 폴백(동작 보존).
   * ChartComposed 의 같은 이름 헬퍼와 규칙이 같아야 한다 — 두 렌더러가 같은 화면에 섞여 쓰인다.
   */
  const colorIndexOf = (field, fallbackIndex) => {
    const orderSeries = isEmpty(allDataSeries) ? pivotConfig?.chartSeries : allDataSeries;
    const i = findIndex(orderSeries, (s) => s.field === field);
    return i >= 0 ? i : fallbackIndex;
  };

  const currentUserData = JSON.parse(localStorage.getItem("user"));

  const formatterByName = (number, name) => {
    if (!isEmpty(formatters)) {
      const yAxisName = isEqual(name, "ALL") ? "ALL" : find(yAxisInfo, info => info.datas.includes(name))?.yAxis;
      const filteredFormatter = formatters.find(formatter => isEqual(formatter.name, yAxisName))?.formatter;
      return filteredFormatter ? filteredFormatter(number) : baseFormatter(number);
    }

    return number;
  }

  const formatterByNameForToolTip = (value, name, series) => {
    let result = value;
    if (!isEmpty(formatters)) {
      const yAxisName = isEqual(name, "ALL") ? "ALL" : find(yAxisInfo, info => info.datas.includes(name))?.yAxis;
      const filteredFormatter = formatters.find(formatter => isEqual(formatter.name, yAxisName))?.formatter;
      result =  filteredFormatter ? filteredFormatter(value) : baseFormatter(value);
    }
    let symbol = "";
    if (chartOptions.symbol) {
      symbol = chartOptions.symbol;
    }
    if (chartOptions.isAmt && currentUserData.currencySymbol) {
      symbol = currentUserData.currencySymbol;
    }
    if (series?.payload && series?.payload.currencySymbol && series?.type) {
      series.type = series?.payload.currencySymbol
    }
    if (series.type) {
      symbol = series.type
    }
    if (chartOptions.isAmt) {
      if (series?.payload && series?.payload.currencySymbol) {
        symbol = series?.payload.currencySymbol;
      }
    }
    if (value < 0) {
      return `-${symbol}${replace(result.toString(), "-", "")}`;
    }
    return symbol+result;
  }


  const setChart = (overrideSeries) => {
    if (!isEmpty(chartData) && !isEmpty(pivotConfig) && !isEmpty(legend)) {
      if (pivotConfig?.chartSeries && multiSeries) {
        const seriesToProcess = overrideSeries ?? pivotConfig.chartSeries;
        const infos = [];
        for (const series of seriesToProcess) {
          const yAxis = series?.groupName ? series.groupName : series.text;
          const idx = findIndex(infos, info => isEqual(info.yAxis, yAxis));
          if (idx > -1) {
            infos[idx].datas.push(series.text);
          } else {
            infos.push({ yAxis, datas: [series.text], seriesFormatter: series?.formatter });
          }
        }
        setYAxisInfo(infos);
        const allFormatter = [];
        for (const info of infos) {
          const { yAxis, seriesFormatter } = info;
          // const { yAxis, datas, seriesFormatter } = info;
          // const valuesByGroup = [];
          // for (const data of datas) {
          //   const values = map(chartData, data).filter(val => !isNull(val) && !isUndefined(val));
          //   valuesByGroup.push(...values);
          // }
          // const sum = valuesByGroup.reduce((prev, current) => prev + current, 0);
          // const avg = valuesByGroup.length > 0 ? sum / valuesByGroup.length : 0;
          // const formatter = seriesFormatter ?? numberUtil.getNumberFormatterByAvg(avg); K, M ,B 단위로 표시 
          const formatter = seriesFormatter ?? numberUtil.formatNumber; // 기본 숫자 포맷
          allFormatter.push({ formatter, name: yAxis });
        }
        setFormatters(allFormatter);
      } else {
        // const allValues = [];
        // for (const itemLegend of legend) {
        //   const values = map(chartData, itemLegend).filter(val => !isNull(val) && !isUndefined(val));
        //   allValues.push(...values);
        // }
        // const sum = allValues.reduce((prev, current) => prev + current, 0);
        // const avg = allValues.length > 0 ? sum / allValues.length : 0;
        // const formatter = numberUtil.getNumberFormatterByAvg(avg);  K, M ,B 단위로 표시 
        const formatter = numberUtil.formatNumber; // 기본 숫자 포맷
        setFormatters([{ formatter, name: "ALL" }]);
      }
    }
  }

    /* 설정된 데이터를 분석하여 그래프의 Style을 조정하기 위함 (기본 Chart Option Margin 및 Y축의 translate X 축) 
    * Series의 orientation이 한축에 2개이상 존재할 경우 해당 축의 길이를 계산하여 Margin 및 Y축의 translate X 축을 조정한다.
    */
    const LARGE_VALUE_BASE_MARGIN = 30;
    const Y_AXIS_TRANSFORM_DEFUALT = 10;
    const chartAndYAxisStyleWhenSeriesOrientationDuplicate = (chartData, dataSeries) => {
      /* dataSeries 및 ChartData가 존재하지 않는다면 스킵 */
      if (isNil(dataSeries) || isEmpty(dataSeries) || isNil(chartData) || isEmpty(chartData)) return;
  
      const newChartOptions = { ...options };
      const ananlysisObj = {};

      let idx = 0;
      const groupInfoObj = {};
      const leftRightSumLength = { left: 0, right: 0 }; /* Left, Right 별 총 길이를 구하여 전체적인 Chart margin을 조정하기 위한 변수 */
      const prevLeftRightLength = { left: 0, right: 0 };
      for (const series of dataSeries) {
        /* 기본값은 Index 순서별 left, right, left, right 반복 */
        let orientation = idx % 2 === 0 ? "left" : "right";

        /* 기본값과 별개로 차트 기준축이 존재한다면 덮어쓰기 */
        if (!isEmpty(series?.orientation)) {
          orientation = series.orientation;
        }
  
        /* 한축에 Group이 묶여있는경우 이미 Group에 셋팅되어있는 축으로 설정. */
        if (!isEmpty(series?.groupName)) {
          if (isEmpty(groupInfoObj?.[series.groupName])) {
            groupInfoObj[series.groupName] = orientation; 
          } else {
            orientation = groupInfoObj[series.groupName];
          }
        }

        idx++;
        const seriesText = series.text;
        const seriesMaxValues = Math.max.apply(null, map(chartData, seriesText));

        const maxValue = isNaN(seriesMaxValues) ? "0" : seriesMaxValues.toString();
        const splitDotValue = maxValue.split(".")[0];
        const valueLength = splitDotValue.length;
  
        /* [yAxisTransform] Left: 음수, Right: 양수, 기본: 0 (Label 및 Tick의 사이즈 때문에 X 축 조정하기 위함)*/
        const yAxisLabelName = series.groupName ? series.groupName : seriesText;
        if (isNil(ananlysisObj[yAxisLabelName]) || isEmpty(ananlysisObj[yAxisLabelName])) {
          ananlysisObj[yAxisLabelName] = {
            orientation,
            valueLength,
            isLarge: valueLength > 7,
            yAxisTransform: 0
          };

          if (orientation === "left") {
            leftRightSumLength.left += valueLength;

            if (prevLeftRightLength.left > 7) {
              const yAxisTransform = -(prevLeftRightLength.left * 2 + Y_AXIS_TRANSFORM_DEFUALT);
              ananlysisObj[yAxisLabelName] = { orientation, valueLength, isLarge: valueLength > 7, yAxisTransform };
            }

            prevLeftRightLength.left = valueLength;
          } else {
            leftRightSumLength.right += valueLength;

            if (prevLeftRightLength.right > 7) {
              const yAxisTransform = (prevLeftRightLength.right * 2 + Y_AXIS_TRANSFORM_DEFUALT);
              ananlysisObj[yAxisLabelName] = { orientation, valueLength, isLarge: valueLength > 7, yAxisTransform };
            }

            prevLeftRightLength.right = valueLength;
          }
        } else {
          const originObj = ananlysisObj[yAxisLabelName];
          const compareValue = originObj.valueLength > valueLength ? originObj.valueLength : valueLength;

          if (orientation === "left") {
            leftRightSumLength.left = leftRightSumLength.left - originObj.valueLength + compareValue;
          } else {
            leftRightSumLength.right = leftRightSumLength.right - originObj.valueLength + compareValue;
          }

          ananlysisObj[yAxisLabelName] = {
            orientation,
            valueLength: compareValue,
            isLarge: compareValue > 7,
            yAxisTransform: originObj.yAxisTransform
          };
        }
      }
  
      if (leftRightSumLength.left > 7) {
        newChartOptions.margin.left = LARGE_VALUE_BASE_MARGIN + leftRightSumLength.left;
      }
  
      if (leftRightSumLength.right > 7) {
        newChartOptions.margin.right = LARGE_VALUE_BASE_MARGIN + leftRightSumLength.right;
      }

      setChartOptions(newChartOptions);
      setSeriesAnanlysisObj(ananlysisObj); /* {orientation: text, length: number, isLarge: boolean, yAxisTransform: number} */
    }

  useEffect(() => {
    if (!isEmpty(chartData) && !isEmpty(pivotConfig) && !isEmpty(legend) && !isEmpty(options)) {
      const chartSeries = pivotConfig?.chartSeries;
      if (toggle) {
        toggle.initial = pivotConfig?.chartSeries[0].type;
      }

      const effectiveSeries = activeMeasures && activeMeasures.length > 0
        ? chartSeries?.filter(s => activeMeasures.includes(s.field))
        : chartSeries;

      /* 설정된 데이터를 분석하여 그래프의 Style을 조정하기 위함 (기본 Chart Option Margin 및 Y축의 translate X 축)
      * Series의 orientation이 한축에 2개이상 존재할 경우 해당 축의 길이를 계산하여 Margin 및 Y축의 translate X 축을 조정한다.
      */
      chartAndYAxisStyleWhenSeriesOrientationDuplicate(chartData, effectiveSeries);

      if (effectiveSeries && multiSeries) {
        const sortedSeries = sortBy(effectiveSeries, ["type"]);
        setSortedLegend(map(sortedSeries, "text"));
        setChart(effectiveSeries);
      } else {
        setSortedLegend(legend);
        setChart();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData, pivotConfig, legend, options, activeMeasures]);

  useEffect(() => {
    if (toggle && pivotConfig?.chartSeries && multiSeries) {
      for (const row of pivotConfig.chartSeries) {
        if (toggle?.except) {
          if (!toggle.except.includes(row.field)) {
            row.type = toggled;
          }
        } else {
          row.type = toggled;
        }
      }
      if (toggle?.initial) {
        toggle.initial = null;
      }
      setChart();
    }
// eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggled]);

  const handleChartConfigOpen = () => {
    setChartConfigOpen(!chartConfigOpen);
  };

  // const CustomTooltip = customTooltip;
  if (chartData && sortedLegend && pivotConfig) {
    const orientationList = [];
    const applyLeftRightTranslateX = { left: 0, right: 0 };
    return (
      <Box sx={{position: "relative", height: "100%"}}>
        <Box sx={{ position: "absolute", right: "0%", top: "0%", zIndex: 99}}>
          {showFullScreen ? <FullScreenButton name="fullScreen" label="fullScreen" fullScreen={fullScreen} setFullScreen={setFullScreen} screenId={screenId} setScreenId={setScreenId} /> : null}
          {showPopup ? <SettingsButton name="chartConfig" label="chartConfig" onClick={handleChartConfigOpen} /> : null}
          {toggle?.type ? <ToggleButton toggleType="chart" initialValue={toggle?.initial ? toggle.initial : null} toggleValue={setToggled}></ToggleButton> : <Box></Box>}
        </Box>
      <ResponsiveContainer width="98%" height="100%" minWidth={chartOptions.minWidth} minHeight={chartOptions.minHeight}>
        <ComposedChart
          data={chartData}
          margin={chartOptions.margin}
          stackOffset={chartOptions.stackOffset ?? "none"}
          onClick={(params) => chartClickEvent ? chartClickEvent(params) : null}
        >
          {/* area 시리즈용 세로 그라데이션 fill (위 0.28 → 아래 0 투명). 렌더 색과 동일 index/색상 규칙. */}
          <defs>
            {chartData.length && pivotConfig?.chartSeries && multiSeries
              ? sortedLegend.map((dataKey, index) => {
                  const series = pivotConfig.chartSeries.find(d => isEqual(d.text, dataKey));
                  if (!series || series.type !== "area") return null;
                  // 그라데이션 색은 area 의 선(stroke)과 반드시 같아야 한다(선은 typeOptions.fill/stroke 를 씀).
                  //   colorByMeasure 로만 계산하면 지정색을 쓴 선과 면이 어긋난다 → 시리즈 지정색을 우선.
                  const to = series?.typeOptions || {};
                  const gc = to.fill || to.stroke || colorByMeasure(series.field, colorIndexOf(series.field, index)) || theme.palette.primary.main;
                  return (
                    <linearGradient key={gradKey(series.field)} id={gradKey(series.field)} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={gc} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={gc} stopOpacity={0} />
                    </linearGradient>
                  );
                })
              : null}
            {chartData.length && !(pivotConfig?.chartSeries && multiSeries) && dataDisplaySeries
              ? sortedLegend.map((dataKey, index) => {
                  const series = dataDisplaySeries.find(d => isEqual(d.text, dataKey));
                  if (!series || series.type !== "area") return null;
                  const gc = colorSet(index) || theme.palette.primary.main;
                  return (
                    <linearGradient key={gradKey(series.field)} id={gradKey(series.field)} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={gc} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={gc} stopOpacity={0} />
                    </linearGradient>
                  );
                })
              : null}
          </defs>
          {/* 세로 격자는 끈다 — 범주축이라 칸 경계는 막대가 이미 말해 주고, 선이 늘면 표처럼 보인다. */}
          <CartesianGrid stroke={gridLine(theme)} vertical={false} />
          <XAxis dataKey="name" padding={chartOptions.xAxisPadding} interval={chartOptions.xAxisInterval} fontSize={CHART_FONT_SIZE} height={chartOptions.xAxisHeight} angle={chartOptions.xAxisAngle} dy={chartOptions.xAxisDy} dx={chartOptions.xAxisDx} tickLine={false} />
          {
            pivotConfig?.chartSeries && multiSeries ? (
              pivotConfig.chartSeries.map((series, index) => {
                const yAxisLabel = series?.groupName ? series.groupName : series.text;
                let position = pivotConfig.yLabelPosition ?? "top";

                let dy = -5;
                if (yAxisList.indexOf(yAxisLabel) !== -1) {
                  return "";
                }
                yAxisList.push(yAxisLabel);

                let yAxisLabelColor = theme.palette.text.primary;
                if (isFilled && !series?.groupName) {
                  let labelColor = colorSet(colorIndexOf(series.field, index));
                  if (series?.typeOptions?.fill || series?.typeOptions?.stroke) {
                    const { fill, stroke } = series.typeOptions;
                    labelColor = fill || stroke;
                  } else {
                    labelColor = colorByMeasure(series.field, colorIndexOf(series.field, index));
                  }

                  yAxisLabelColor = labelColor;
                }

                const obj = seriesAnanlysisObj?.[yAxisLabel]; /* {yAxisTransform: number} */
                const applyOrientation = obj?.orientation;
                /* 축이 여러개 존재하는지 판단하는 로직 Y축 이름이 길면 Text가 겹칠 수 있으므로 하단에 출력  */
                const grpNumber = orientationList.filter(orit => orit === applyOrientation).length % 2;
                if (grpNumber === 1) {
                  position = "bottom";
                  dy = 5;
                }
                orientationList.push(applyOrientation);

                let translateX = 0;                
                if (!isNil(obj) && !isEmpty(obj)) {
                  const { yAxisTransform } = obj;
                  const cumulateTransform = applyOrientation === "left" ? applyLeftRightTranslateX.left : applyLeftRightTranslateX.right;
                  translateX = yAxisTransform + cumulateTransform;
                  if (applyOrientation === "left") {
                    applyLeftRightTranslateX.left += yAxisTransform;

                    /* 왼쪽에 위치한다면 translateX 는 음수값이 되어야함. */
                    if (translateX > 0) {
                      translateX = -translateX;
                    }
                  } else {
                    applyLeftRightTranslateX.right += yAxisTransform;

                    /* 오른쪽에 위치한다면 translateX 는 양수값이 되어야함. */
                    if (translateX < 0) {
                      translateX = Math.abs(translateX);
                    }
                  }
                }

                let labelDX = applyOrientation === "left" ? 15 : -15;
                if (translateX !== 0) {
                  labelDX = translateX / 2;
                }

                return [
                  <YAxis label={{ value: yAxisLabel, position, dy, dx: labelDX, fontSize: CHART_FONT_SIZE, fill: yAxisLabelColor }}
                    yAxisId={yAxisLabel}
                    fontSize={FS.BODY}
                    domain={applyOrientation === "right" && chartOptions.yAxisDomainRight ? chartOptions.yAxisDomainRight : chartOptions.yAxisDomain}
                    ticks={applyOrientation === "right" && chartOptions.yAxisTicksRight ? chartOptions.yAxisTicksRight : (chartOptions.yAxisTicks ?? undefined)}
                    tickFormatter={number => formatterByName(number, series.text)}
                    orientation={applyOrientation}
                    style={{ transform: `translate(${translateX}px, 0px)` }}
                    key={`${yAxisLabel}_YAxis`}/>,
                  <ReferenceLine key={`${yAxisLabel}_Line`} y={0} yAxisId={yAxisLabel} stroke="#999999" />,
                  <ReferenceLine key={`${yAxisLabel}_ReferenceLineX`} x={referenceX ? referenceX.value : null} label={{ value: referenceX ? referenceX.title : null, position: "top", fill: "#595959", fontSize: FS.SUB }} yAxisId={yAxisLabel} stroke="#8884d8" fontSize={5} />,
                  <ReferenceLine key={`${yAxisLabel}_ReferenceLineY`} y={referenceY ? referenceY.value : null} label={{ value: referenceY ? referenceY.title : null, position: "top", fill: "#595959", fontSize: FS.SUB }} yAxisId={yAxisLabel} stroke="red" />
                ];
              })
            ) : [ // for case :: MonthlyBySku
              <YAxis label={{ value: pivotConfig.yAxisLabel, position: 'top', dy: -5, fontSize: CHART_FONT_SIZE }}
                yAxisId="left"
                fontSize={FS.BODY}
                domain={chartOptions.yAxisDomain}
                tickFormatter={number => formatterByName(number, "ALL")}
                key={`${pivotConfig.yAxisLabel}_YAxis`} />,
              <ReferenceLine key={`${pivotConfig.yAxisLabel}_Line`} y={0} yAxisId={"left"} stroke="#999999" />,
              <ReferenceLine key={`${pivotConfig.yAxisLabel}_ReferenceLineX`} x={referenceX ? referenceX.value : null} label={{ value: referenceX ? referenceX.title : null, position: "top", fill: "#595959", fontSize: FS.SUB }} yAxisId="left" stroke="#8884d8" fontSize={5} />,
              <ReferenceLine key={`${pivotConfig.yAxisLabel}_ReferenceLineY`} y={referenceY ? referenceY.value : null} label={{ value: referenceY ? referenceY.title : null, position: "top", fill: "#595959", fontSize: FS.SUB }} yAxisId="left" stroke="red" />,
            ]
          }
          <Tooltip isAnimationActive={tooltipAnimation} formatter={pivotConfig?.chartSeries ? (number, test, series) => formatterByNameForToolTip(number, test, series) : number => formatterByNameForToolTip(number, "ALL")} contentStyle={chartOptions?.tooltipContentStyle} content={customTooltip ? customTooltip : <AgStyleTooltipContent />} />
          {isFilled ? <Legend verticalAlign="top" align="right" /> : <Legend wrapperStyle={{bottom: brush ? 20 : 30}}/>}
          {chartData.length && sortedLegend.map((dataKey, index) => {
            if (pivotConfig?.chartSeries && multiSeries) {
              const series = pivotConfig.chartSeries.find(data => isEqual(data.text, dataKey));
              if (series) {
                const yAxisLabel = series?.groupName ? series.groupName : series.text;
                const seriesColor = colorByMeasure(series.field, colorIndexOf(series.field, index));
                const typeOptions = series?.typeOptions;
                let typeOptionObject = {...typeOptions};

                if (Object.keys(typeOptionObject).includes("stackId")) {
                  typeOptionObject = {...typeOptionObject, stackId : `${typeOptionObject.stackId}-${series.type}`}
                }
                if (series.type === "bar") {
                  if (Object.keys(typeOptionObject).includes("stroke")) {
                    typeOptionObject.fill = typeOptionObject.stroke;
                  }
                  return (
                    <Bar yAxisId={yAxisLabel}
                      dataKey={dataKey}
                      maxBarSize={MAX_BAR_SIZE}
                      radius={Object.keys(typeOptionObject).includes("stackId") ? undefined : BAR_RADIUS}
                      fill={seriesColor}
                      key={dataKey}
                      tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                      {...typeOptionObject} />
                  )
                } else if (series.type === "area") {
                  if (Object.keys(typeOptionObject).includes("stroke") || Object.keys(typeOptionObject).includes("fill")) {
                    const color = Object.keys(typeOptionObject).includes("stroke") ? typeOptionObject.stroke : typeOptionObject.fill;
                    typeOptionObject.fill = color;
                    typeOptionObject.stroke = color;
                  }
                  delete typeOptionObject.fill;   // 그라데이션 fill 이 이기도록 solid fill 제거
                  return (
                    <Area
                      yAxisId={yAxisLabel}
                      dataKey={dataKey}
                      stroke={seriesColor}
                      key={dataKey}
                      tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                      {...typeOptionObject}
                      fill={`url(#${gradKey(series.field)})`}
                      fillOpacity={1} />
                  )
                } else {
                  if (Object.keys(typeOptionObject).includes("fill")) {
                    typeOptionObject.stroke = typeOptionObject.fill;
                  }
                  /*
                   * dashed = 점선 라인(ChartComposed 와 같은 규약, 패턴은 DASH_PATTERN 단일 출처).
                   * ⚠️ 누적 차트에서는 line 과 마찬가지로 **쌓이지 않는다** — 위 stackId 가
                   *   `${stackId}-${series.type}` 라 타입별로 스택이 갈리기 때문이다(의도된 동작).
                   */
                  return (
                    <Line yAxisId={yAxisLabel}
                      dataKey={dataKey}
                      type="linear"
                      activeDot={{ r: 4 }}
                      dot={{ r: 1.5, fill: seriesColor, strokeWidth: 0 }}
                      stroke={seriesColor}
                      strokeWidth={1}
                      strokeDasharray={series.type === "dashed" ? DASH_PATTERN : undefined}
                      key={dataKey}
                      tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                      {...typeOptionObject} />
                  )
                }
              }

              return "";
            } else { // for case :: MonthlyBySku
              if (dataDisplaySeries) {
                const series = dataDisplaySeries.find(orgData => isEqual(orgData.text, dataKey));
                if (series) {
                  switch (series.type) {
                    case "area" :
                      return <Area
                        yAxisId="left"
                        dataKey={dataKey}
                        stroke={colorSet(index)}
                        fill={`url(#${gradKey(series.field)})`}
                        fillOpacity={1}
                        tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                        key={dataKey} />
                    case "line" :
                      return <Line yAxisId="left"
                        dataKey={dataKey}
                        type="linear"
                        activeDot={{ r: 4 }}
                        dot={{ r: 1.5, fill: colorSet(index), strokeWidth: 0 }}
                        stroke={colorSet(index)}
                        strokeWidth={1}
                        tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                        key={dataKey} />
                    default : 
                    return <Bar yAxisId="left"
                      dataKey={dataKey}
                      maxBarSize={MAX_BAR_SIZE}
                      radius={BAR_RADIUS}
                      fill={colorSet(index)}
                      tooltipType={CodeUtil.amtCurrencyMeasure.includes(series.field) ? currentUserData.currencySymbol : ""}
                      key={dataKey} />
                  }
                }
              }
              return "";
            }
          })}
            {
              brush ? <Brush
                dataKey={"name"}
                height={20}
                stroke={theme.palette.primary.main}
              /> : ""
            }
        </ComposedChart>
      </ResponsiveContainer>
      <ChartCommonConfigModal
        open={chartConfigOpen}
        setOpen={handleChartConfigOpen}
        viewName={viewName}
        dataSeries={dataOrgSeries}
        colorOrderSeries={allDataSeries}
        modalResultData={setChartModalResultData}
        chartId={chartId}
        multiSeries={multiSeries}
      />
      </Box>
    );
  }

  return <div />;
}

export default React.memo(ComposedBarLineChart, isEqual);